/**
 * CIPAA Create Page
 *
 * Page for creating new CIPAA elections.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { PageShell } from '../../components';
import { CipaaForm } from './components';
import { CipaaFormData, CreateCipaaRequest } from '../../types/cipaa';
import { cipaaService } from '../../services/cipaaService';

const CipaaCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (formData: CipaaFormData) => {
    if (!formData.companyId) {
      setError('Selecione uma empresa');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const requestData: CreateCipaaRequest = {
        name: formData.name,
        companyId: formData.companyId as number,
        accessKey: formData.accessKey ?? undefined,
        startDate: formData.startDate,
        startTime: formData.startTime,
        endDate: formData.endDate,
        endTime: formData.endTime,
        electionDate: formData.electionDate ?? undefined,
        registrationOpenDate: formData.registrationOpenDate ?? undefined,
        candidateAnnouncementDate: formData.candidateAnnouncementDate ?? undefined,
        resultsAnnouncementDate: formData.resultsAnnouncementDate ?? undefined,
        termStartDate: formData.termStartDate ?? undefined,
        termEndDate: formData.termEndDate ?? undefined,
        registrationLocation: formData.registrationLocation ?? undefined,
        responsible: formData.responsible ?? undefined,
        effectiveMembers: formData.effectiveMembers ?? 0,
        alternateMembers: formData.alternateMembers ?? 0,
        employeeCount: formData.employeeCount,
        riskLevel: formData.riskLevel,
      };

      const election = await cipaaService.create(requestData);
      enqueueSnackbar('Eleição criada com sucesso', { variant: 'success' });
      navigate(`/cipaa/${election.id}`);
    } catch (err: any) {
      setError(err.message ?? 'Erro ao criar eleição');
      enqueueSnackbar('Erro ao criar eleição', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/cipaa');
  };

  const breadcrumbItems = [
    { label: 'Início' },
    { label: 'Segurança do Trabalho' },
    { label: 'Eleições CIPAA' },
    { label: 'Nova Eleição' },
  ];

  return (
    <PageShell breadcrumbs={breadcrumbItems} title="Nova Eleição CIPAA">
      {/* Form Card */}
      <Card>
        <CardHeader title="Dados da Eleição" />
        <CardContent>
          <CipaaForm
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
            error={error}
          />
        </CardContent>
      </Card>
    </PageShell>
  );
};

export default CipaaCreatePage;
